import { type JSX, useCallback, useId } from 'react';
import { FocusOn } from 'react-focus-on';
import {
  getDialogAriaProps,
  getDialogIds,
} from '@common/components/controls/ariaHelpers/dialogAriaHelpers';
import Button from '@common/components/controls/Button';
import Translated from '@common/components/Translated';
import { useDialogLifecycle } from '@common/hooks/useDialogLifecycle';
import classnames from '@common/utils/ui/classnames';
import '@common/styles/components/popUp.pcss';

/**
 * Props for the {@link PopUp} component.
 *
 * @see {@link PopUp} for usage examples and lifecycle documentation.
 */
export interface PopUpProps {
  /**
   * Controls the visibility of the popup. When toggled from `true` to `false`,
   * the popup plays a fade-out animation before unmounting from the DOM.
   *
   * Defaults to `true` so the popup renders immediately when first mounted.
   *
   * **Important:** The parent component must own this state and set it to `false`
   * inside the `close` (or `onClose`) callback. The PopUp does **not** manage
   * visibility internally — it only _requests_ close via the callback.
   */
  show?: boolean;
  /** The popup title, rendered inside the heading element (see `titleTag`). */
  title: JSX.Element;
  /** The popup body content, rendered between the title and the button row. */
  children: JSX.Element;
  /** The primary action button rendered in the button row (e.g., "Save", "Download"). */
  actionButton: JSX.Element;
  /**
   * Callback invoked immediately when close is requested (cancel button or Escape key).
   * Use this to update parent state (e.g., `setShow(false)`).
   *
   * If both `close` and `onClose` are provided, `close` takes precedence.
   */
  onClose?: () => void;
  /**
   * Alias for `onClose`. Takes precedence over `onClose` when both are provided.
   * Exists for backward compatibility with older PopUp call sites.
   */
  close?: () => void;
  /**
   * Callback invoked **after** the close animation finishes and the dialog
   * is fully hidden. Use this for cleanup that depends on the popup being
   * gone from the DOM (e.g., resetting form state, refocusing a trigger element).
   *
   * Not called if the popup is unmounted by its parent before the animation completes.
   */
  onClosed?: () => void;
  /** Additional CSS class(es) applied to the `<dialog>` element. */
  className?: string;
  /** When `true`, the cancel button is hidden. The action button row still renders. */
  noCancelButton?: boolean;
  /**
   * @deprecated Use `type="narrow"` instead.
   * Renders the popup in a narrower layout. Ignored when `type` is set.
   */
  narrow?: boolean;
  /**
   * Size variant for the popup.
   * - `'narrow'` — reduced max-width for simple dialogs.
   * - `'small'`  — even smaller, for compact confirmations.
   *
   * Takes precedence over the deprecated `narrow` prop.
   */
  type?: 'narrow' | 'small';
  /** When `true`, the cancel button is rendered but disabled (grayed out, not clickable). */
  disableCancel?: boolean;
  /**
   * HTML heading level for the title. Defaults to `'h2'`.
   * Choose based on the heading hierarchy of the page where the popup appears.
   */
  titleTag?: 'h2' | 'h3' | 'h4';
}

/**
 * Modal dialog component built on the native `<dialog>` element with animated
 * open/close transitions, focus trapping, and accessible markup.
 *
 * ## Lifecycle
 *
 * ```
 * show=true          show=false              animation ends
 *    │                   │                        │
 *    ▼                   ▼                        ▼
 * shouldRender=true   internalIsOpen=false     modal.close()
 * showModal()         closing attribute set    shouldRender=false
 * FocusOn enabled     fade-out animation       onClosed() called
 *                     FocusOn disabled          component unmounts
 *                     close/onClose called
 * ```
 *
 * 1. **Mount & open:** When `show` becomes `true`, the `<dialog>` is rendered
 *    into the DOM and `showModal()` is called (creates a top-layer modal with
 *    backdrop). Focus is trapped inside via `FocusOn`.
 *
 * 2. **Close requested:** The user clicks Cancel, presses Escape, or the parent
 *    sets `show=false`. The `close` (or `onClose`) callback fires so the parent
 *    can set its state to `false`.
 *
 * 3. **Close animation:** The `<dialog>` receives a `closing` attribute which
 *    triggers the `popup-fade-out` CSS animation. `FocusOn` is disabled
 *    (`enabled={show}`) so focus is released during the animation.
 *
 * 4. **Unmount:** When `popup-fade-out` finishes, `shouldRender` becomes `false`,
 *    the `<dialog>` is removed from the DOM, and `onClosed` fires.
 *
 * ## Focus management
 *
 * Focus trapping is handled by `react-focus-on` (`FocusOn`), **not** by the
 * native `<dialog>`. The native Escape key handling is disabled
 * (`disableNativeCancel: true`); instead, `FocusOn.onEscapeKey` calls
 * `handleClose` to avoid double-triggering.
 *
 * ## Usage with parent state
 *
 * The parent **must** own the boolean state that drives `show` and flip it
 * inside the `close` callback:
 *
 * ```tsx
 * const [showPopUp, setShowPopUp] = useState(false);
 *
 * <PopUp
 *   show={showPopUp}
 *   close={() => setShowPopUp(false)}
 *   title={<Translated id="my.title" />}
 *   actionButton={<Button onClick={handleSave}>Save</Button>}
 * >
 *   <p>Content here</p>
 * </PopUp>
 * ```
 */
function PopUp({
  show = true, // Default to true for PopUp drop-in replacement
  title,
  children,
  actionButton,
  onClose,
  close, // PopUp compatibility
  onClosed,
  className = '',
  noCancelButton,
  narrow,
  type,
  disableCancel,
  titleTag: TitleTag = 'h2',
}: PopUpProps) {
  const dialogId = useId();
  const { titleId } = getDialogIds(dialogId);
  const ariaProps = getDialogAriaProps({
    titleId,
    isModal: true,
  });

  // Use close or onClose (close takes precedence for PopUp compatibility)
  const closeHandler = useCallback(() => {
    (close || onClose)?.();
  }, [close, onClose]);

  // Determine size variant (type prop takes precedence over narrow prop)
  const isNarrow = type === 'narrow' || (narrow && !type);
  const isSmall = type === 'small';

  const { dialogRef, shouldRender, handleClose } = useDialogLifecycle({
    isOpen: show,
    onClose: closeHandler,
    onClosed,
    animationType: 'animation',
    animationEndCheck: (event) => (event as AnimationEvent).animationName === 'popup-fade-out',
    disableNativeCancel: true, // Escape is handled via FocusOn's onEscapeKey to prevent double-trigger
  });

  return shouldRender
    ? (
      <dialog
        {...ariaProps}
        ref={dialogRef}
        className={classnames({
          [className]: !!className,
          popup: true,
          'popup--narrow': isNarrow,
          'popup--small': isSmall,
        })}
        tabIndex={-1}
      >
        {
          /* Use 'show' instead of 'shouldRender' for enabled prop.
            During close animation, shouldRender=true (dialog stays in DOM) but show=false (user closed it).
            Focus lock must be disabled when show=false to release focus properly during closing animation. */
        }
        <FocusOn
          enabled={show}
          onEscapeKey={handleClose}
          scrollLock={false}
          preventScrollOnFocus
          className="popup__focus-wrapper"
        >
          <TitleTag id={titleId} className="popup__title">
            {title}
          </TitleTag>
          <div className="popup__content">
            {children}
            <div id="dialog-overlays" />
          </div>
          <div className="popup__buttons">
            {!noCancelButton && (
              <Button
                type="button"
                mode="secondary"
                onClick={handleClose}
                onMouseDown={(e) => e.preventDefault()}
                disabled={disableCancel}
                data-testid="popupCancelButton"
              >
                <Translated id="common.actions.cancel" />
              </Button>
            )}
            {actionButton}
          </div>
        </FocusOn>
      </dialog>
    )
    : null;
}

export default PopUp;
